import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  lastPlanKindFromSubscriptions,
  planLockVerdict,
  type PlanKind,
  type PlanStateFields,
} from '../../lib/plan-lock/plan-lock-rules';

// Phase 1-D — Agency Portal identity payload as returned by GET /api/v1/me.
// Phase 8-E added optional `brand_*` aliases; the legacy shape is kept for
// backwards compatibility.
export interface AgencyAccessibleShop {
  slug: string;
  name: string;
  permission_level: string;
  // Phase 8-E — optional alias fields. New backend payloads may carry these
  // alongside (or instead of) the legacy `slug`/`name`.
  brand_id?: number;
  brand_slug?: string;
  brand_name?: string;
}

// Phase 8-E — new-shape brand entry. Returned via `accessible_brands` on
// payloads that opt into the brand alias.
export interface AgencyAccessibleBrand {
  brand_id: number;
  brand_slug: string;
  brand_name: string;
  permission_level: string;
}

// Phase 9-D — backend may return a list of (shop, platform) tuples that
// have at least one active legacy Slack row AND no replacement non-legacy
// row. Drives the in-app reconnect banner on the agency Dashboard.
export interface NeedsLegacyReconnectEntry {
  shop_slug: string;
  platform: 'slack' | string;
}

export interface AgencyEmployeeIdentity {
  type: 'employee' | 'owner' | string;
  employee_id?: string | null;
  organisation_id?: string | null;
  email?: string | null;
  name?: string | null;
  role?: string | null;
  // Phase 1 (custom roles) — flat list of the acting employee's *effective*
  // capability keys, as returned by GET /api/v1/me. This is the live source of
  // truth for `can()` (see `lib/rbac/permissions.ts`); when absent (older
  // backends), the RBAC layer falls back to the hardcoded ROLE_CAPABILITIES
  // matrix keyed on `role`. Owner = all 21 keys.
  capabilities?: string[];
  accessible_shops?: AgencyAccessibleShop[];
  // Phase 8-H — drives the agency-facing UI relabel (Brand vs Business Unit).
  account_type?: 'agency' | 'enterprise' | string;
  // Phase 8-E — optional alias collection of the same data under the brand name.
  accessible_brands?: AgencyAccessibleBrand[];
  // Phase 9-D — Slack reconnect surface; empty array (or undefined when the
  // backend hasn't been updated yet) means nothing to nudge about.
  needs_legacy_reconnect?: NeedsLegacyReconnectEntry[];
  // Plan state of the organisation. `plan_enforced` says whether central-api
  // refuses organisations without a plan; it is absent on older backends, and
  // the app only locks when it is `true` (see `lib/plan-lock`).
  plan_status?: 'active' | 'trialing' | 'expired' | 'none' | string;
  has_active_plan?: boolean;
  plan_enforced?: boolean;
}

// Phase 6-C — Client Portal magic-link identity returned by /client-portal/auth + /me
export interface ClientPortalIdentity {
  email: string;
  name?: string | null;
  shop_slug: string;
  shop_name: string;
  shop_brand_color?: string | null;
}

const initialState = {
  authorization: {
    accessToken: {
      sellerToken: '',
      type: 'Bearer',
      expiresOn: null as any,
      whiteLabelSellerTokens: {} as any,
      currentWhiteLabelSellerToken: '',
    },
    isAdminSession: false as boolean,
    webAuthn: {
      isWebAuthnAvailableInAccount: false as boolean,
      isLoggedInViaWebAuthn: false as boolean,
      isWebAuthnRegistrationSkipped: false as boolean,
      hideWebAuthnPrompt: 0 as number,
    },
    whiteLabelRedirectPath: '',
    // The plan lock: 'pending' until central-api has said something, 'true'
    // when the organisation has no active plan or trial while plans are
    // enforced, 'false' otherwise. Read it through `selectIsPlanLocked`.
    subscriptionLock: 'pending' as 'pending' | 'true' | 'false',
    // Why it is locked (`expired` once a trial or paid period lapsed,
    // otherwise `none`) and the sentence central-api sent with the refusal.
    planStatus: null as string | null,
    planLockMessage: null as string | null,
    // The kind of plan the organisation held last, from its own subscription
    // rows in this sign-in's seller-init. Picks the wording of the lock notice.
    lastPlanKind: null as PlanKind | null,
  },
  // Phase 1-D — Agency Portal auth state. Kept separate from `authorization`
  // (which is the legacy seller-token store) so the two flows don't collide.
  // NEVER persisted — per CLAUDE.md, agency Bearer tokens live in Redux only.
  agency: {
    accessToken: '' as string,
    employeeIdentity: null as AgencyEmployeeIdentity | null,
    // Phase B — true while the boot-time `/me` cookie-restore is in flight.
    // Gates the splash so a cookie-backed reload doesn't flash the login
    // screen before the agency session resolves. Always true on a cold boot
    // (this slice is never persisted), and flipped false once `/me` settles.
    sessionResolving: true as boolean,
  },
  // Phase 6-C — Client Portal magic-link session. Lives in Redux ONLY (the
  // store-level redux-persist whiteList only persists `user`, so the `auth`
  // slice — including this sub-slice — is never written to localStorage).
  clientPortal: {
    accessToken: null as string | null,
    expiresAt: null as number | null, // epoch ms
    identity: null as ClientPortalIdentity | null,
  },
  // Phase 6-C — Admin Portal session. For Phase 6 this is just a Bearer
  // token paste form OR reuse of the existing seller JWT. Kept in Redux only.
  admin: {
    accessToken: '' as string,
  },
};

// `expired` (a trial that ended) is the more specific reason: once one source
// has said it, a later `none` from another source does not replace it.
function mergePlanStatus(current: string | null, incoming: unknown) {
  if (current === 'expired' || typeof incoming !== 'string') return current;
  return incoming;
}

function applyPlanFields(
  authorization: {
    subscriptionLock: 'pending' | 'true' | 'false';
    planStatus: string | null;
    planLockMessage: string | null;
  },
  fields: PlanStateFields | null | undefined,
  mayLock: boolean
) {
  const verdict = planLockVerdict(fields);
  if (verdict === 'unlocked') {
    authorization.subscriptionLock = 'false';
    authorization.planStatus = null;
    authorization.planLockMessage = null;
  } else if (verdict === 'locked') {
    authorization.planStatus = mergePlanStatus(
      authorization.planStatus,
      fields?.plan_status
    );
    if (mayLock) {
      authorization.subscriptionLock = 'true';
    }
  }
  // 'unknown' changes nothing: missing data never locks and never unlocks.
}

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAccessToken: (
      state,
      action: PayloadAction<{
        sellerToken: any;
        type: string;
        expiresOn: any;
        whiteLabelSellerTokens?: any;
        currentWhiteLabelSellerToken?: any;
      }>
    ) => {
      state.authorization.accessToken.sellerToken =
        action.payload.sellerToken ?? undefined;
      state.authorization.accessToken.type = action.payload.type;
      state.authorization.accessToken.expiresOn = action.payload.expiresOn;
      state.authorization.accessToken.whiteLabelSellerTokens =
        action.payload?.whiteLabelSellerTokens;
      state.authorization.accessToken.currentWhiteLabelSellerToken =
        action.payload?.currentWhiteLabelSellerToken;
    },

    setIsAdminSession: (state, action: PayloadAction<boolean>) => {
      state.authorization.isAdminSession = action.payload;
    },

    setWhiteLabelRedirectPath: (state, action: PayloadAction<string>) => {
      state.authorization.whiteLabelRedirectPath = action.payload;
    },

    setIsWebAuthnAvailableInAccount: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.authorization.webAuthn.isWebAuthnAvailableInAccount =
        action.payload;
    },

    setIsLoggedInViaWebAuthn: (state, action: PayloadAction<boolean>) => {
      state.authorization.webAuthn.isLoggedInViaWebAuthn = action.payload;
    },

    setIsWebAuthnRegistrationSkipped: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.authorization.webAuthn.isWebAuthnRegistrationSkipped =
        action.payload;
    },

    setHideWebAuthnPrompt: (state, action: PayloadAction<number>) => {
      state.authorization.webAuthn.hideWebAuthnPrompt = action.payload;
    },

    clearAuthSlice: (state) => {
      state.authorization = {
        accessToken: {
          sellerToken: '',
          type: 'Bearer',
          expiresOn: null,
          whiteLabelSellerTokens: {},
          currentWhiteLabelSellerToken: '',
        },
        isAdminSession: false,
        webAuthn: {
          isWebAuthnAvailableInAccount: false,
          isLoggedInViaWebAuthn: false,
          isWebAuthnRegistrationSkipped: false,
          hideWebAuthnPrompt: 0,
        },
        subscriptionLock: 'pending',
        planStatus: null,
        planLockMessage: null,
        lastPlanKind: null,
        whiteLabelRedirectPath: '',
      };
    },

    // Plan state from seller-init. It is read after the organisation's first
    // trial has been granted, so its answer is applied as it is.
    applyPlanState: (
      state,
      action: PayloadAction<
        (PlanStateFields & { subscription?: unknown }) | null | undefined
      >
    ) => {
      applyPlanFields(state.authorization, action.payload, true);
      // Seller-init also lists the organisation's subscription rows: they
      // say whether the plan that lapsed was a trial or a paid plan.
      const lastPlanKind = lastPlanKindFromSubscriptions(
        action.payload?.subscription
      );
      if (lastPlanKind !== undefined) {
        state.authorization.lastPlanKind = lastPlanKind;
      }
    },

    // A gated call was refused with 402 `no_active_plan`.
    lockForNoActivePlan: (
      state,
      action: PayloadAction<{ plan_status?: unknown; message?: unknown }>
    ) => {
      const { plan_status, message } = action.payload ?? {};
      state.authorization.subscriptionLock = 'true';
      state.authorization.planStatus = mergePlanStatus(
        state.authorization.planStatus,
        plan_status
      );
      state.authorization.planLockMessage =
        typeof message === 'string' ? message : null;
    },

    // ----- Phase 1-D — Agency Portal actions -----
    setAgencyAccessToken: (state, action: PayloadAction<string>) => {
      state.agency.accessToken = action.payload ?? '';
    },
    setEmployeeIdentity: (
      state,
      action: PayloadAction<AgencyEmployeeIdentity | null>
    ) => {
      state.agency.employeeIdentity = action.payload;
      // `/me` carries the plan state. A session that also holds a seller token
      // takes "locked" from seller-init instead: `/me` can be answered just
      // before seller-init grants a new organisation its first trial, and
      // would then lock an account that is about to start trialing.
      applyPlanFields(
        state.authorization,
        action.payload,
        !state.authorization.accessToken.sellerToken
      );
    },
    clearAgencyAuth: (state) => {
      state.agency.accessToken = '';
      state.agency.employeeIdentity = null;
      state.authorization.subscriptionLock = 'pending';
      state.authorization.planStatus = null;
      state.authorization.planLockMessage = null;
      state.authorization.lastPlanKind = null;
      // Logout is a terminal state — nothing left to resolve.
      state.agency.sessionResolving = false;
    },
    // Phase B — toggled by the boot-time cookie-restore effect in App.tsx.
    setAgencySessionResolving: (state, action: PayloadAction<boolean>) => {
      state.agency.sessionResolving = action.payload;
    },

    // ----- Phase 6-C — Client Portal actions -----
    setClientPortalSession: (
      state,
      action: PayloadAction<{
        accessToken: string;
        expiresAt: number;
        identity: ClientPortalIdentity;
      }>
    ) => {
      state.clientPortal.accessToken = action.payload.accessToken;
      state.clientPortal.expiresAt = action.payload.expiresAt;
      state.clientPortal.identity = action.payload.identity;
    },
    setClientPortalIdentity: (
      state,
      action: PayloadAction<ClientPortalIdentity | null>
    ) => {
      state.clientPortal.identity = action.payload;
    },
    clearClientPortalSession: (state) => {
      state.clientPortal.accessToken = null;
      state.clientPortal.expiresAt = null;
      state.clientPortal.identity = null;
    },

    // ----- Phase 6-C — Admin Portal actions -----
    setAdminAccessToken: (state, action: PayloadAction<string>) => {
      state.admin.accessToken = action.payload ?? '';
    },
    clearAdminAuth: (state) => {
      state.admin.accessToken = '';
    },
  },
});

export const authReducer = authSlice.reducer;

export const {
  setAccessToken,
  setIsAdminSession,
  clearAuthSlice,
  applyPlanState,
  lockForNoActivePlan,
  setWhiteLabelRedirectPath,
  setIsWebAuthnAvailableInAccount,
  setIsLoggedInViaWebAuthn,
  setIsWebAuthnRegistrationSkipped,
  setHideWebAuthnPrompt,
  setAgencyAccessToken,
  setEmployeeIdentity,
  clearAgencyAuth,
  setAgencySessionResolving,
  setClientPortalSession,
  setClientPortalIdentity,
  clearClientPortalSession,
  setAdminAccessToken,
  clearAdminAuth,
} = authSlice.actions;
